import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { useApi } from "@/api/hooks/useApi";
import type { SimpleProjectDto, UserRoleDto, UsersMultipleCreateRequest } from "@/api/types";
import { Button } from "@/components/Button/Button";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormContent } from "@/components/Form/FormContent";
import { FormField } from "@/components/Form/FormField";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormMultiSelect } from "@/components/Form/FormMultiSelect";
import { FormSelect } from "@/components/Form/FormSelect";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { DocumentPaper } from "@/components/Paper/DocumentPaper";
import { useSuggestedEmailFix } from "@/helpers/email-validation";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import {
  createEmailValidRule,
  createImageSizeRule,
  createImageTypeRule,
  createPhoneNumberStringRule,
  createRequiredStringRule,
} from "@/helpers/rules";
import { sortAlphabetically } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { QUERY_KEYS } from "@/query-keys";
import { Trans, useTranslation } from "@/translations";

interface AddAdminFormValues {
  firstName: string;
  lastName: string;
  mobileNumber: string;
  email: string;
  occupation?: string;
  roleType: UserRoleDto["type"];
  avatar: FormImage[];
  hideChat: boolean;
  isOnlyAllowedOwnTickets: boolean;
  isAssignableToTickets: boolean;
  onboardingComplete: boolean;
  projects: SimpleProjectDto[];
}

const MAX_CHARACTERS = 255;

export function Layout(): React.ReactNode {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { t } = useTranslation();
  const posthog = usePostHog();
  const api = useApi();
  const { uploadFormImage } = useUploadImage();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const sessionUser = useSessionUser();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const roleTypes = [
    "projectOwner",
    "readOnly",
    "maintenance",
    "caretaker",
    "servicePartner",
  ] as const satisfies UserRoleDto["type"][];

  const form = useForm<AddAdminFormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      mobileNumber: "",
      email: "",
      roleType: roleTypes[0],
      avatar: [],
      hideChat: true,
      isOnlyAllowedOwnTickets: false,
      onboardingComplete: true,
    },
  });
  const {
    data: allowedProjects,
    isPending: isPendingAllowedProjects,
    error: errorAllowedProjects,
  } = useQuery({
    queryKey: QUERY_KEYS.PROJECT_INVITE_LIST,
    queryFn: () => api.getUsersMultipleProjectsCanInviteV1(),
    select: (res) => {
      const { projects } = commonAPIDataSelector(res);

      return projects;
    },
  });
  const addUsersMutation = useMutation({
    mutationFn: api.postUsersMultipleV1,
    onSuccess: (_, payload) => {
      payload.projectIds.forEach((currProjectId) => {
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.USERS_QUERY(currProjectId),
        });
      });

      showFlashToast({ type: "success", title: t("page.user-detail.invite-user.success") });
    },
    onError: () => {
      showFlashToast({ type: "error", title: t("page.user-detail.invite-user.error") });
    },
  });

  const getRoleTypeLabel = (roleType: UserRoleDto["type"]) => {
    switch (roleType) {
      case "resident":
        return t("model.role.type.resident");
      case "projectOwner":
        return t("model.role.type.project-owner");
      case "readOnly":
        return t("model.role.type.readOnly");
      case "maintenance":
        return t("model.role.type.maintenance");
      case "caretaker":
        return t("model.role.type.caretaker");
      case "servicePartner":
        return t("model.role.type.service-partner");
      default:
        console.error(`Unknown user role type "${roleType}"`);

        return "";
    }
  };

  const handleSubmit = async (formValues: AddAdminFormValues) => {
    setIsSubmitting(true);

    const data: UsersMultipleCreateRequest = {
      email: formValues.email.trim(),
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      mobileNumber: formValues.mobileNumber ?? "",
      roleType: formValues.roleType,
      projectIds: formValues.projects.map((project) => project.id),
      hideChat: formValues.hideChat,
      isOnlyAllowedOwnTickets: formValues.isOnlyAllowedOwnTickets,
      isAssignableToAllTickets: formValues.isAssignableToTickets,
      onboardingCompleted: formValues.onboardingComplete,
    };

    if (formValues.avatar.length > 0) {
      const uploadedImage = await uploadFormImage(formValues.avatar[0]);

      data.avatarId = uploadedImage?.id;
    }

    await addUsersMutation.mutateAsync(data);

    form.reset();
    setIsSubmitting(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const email = useWatch({ control: form.control, name: "email" });
  const emailSuggestion = useSuggestedEmailFix(email);

  const error = errorAllowedProjects;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isLoading = isPendingAllowedProjects;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <DocumentPaper title={t("page.add-admins.title")} subTitle={t("page.add-admins.subtitle")}>
      <PageGrid.Grid>
        <PageGrid.Item size="66%">
          <Form
            className="flex w-full flex-col bg-white p-4 sm:rounded-lg 2xl:p-6"
            formMethods={form}
            onSubmit={handleSubmit}
          >
            <FormContent>
              <FormField label={t("page.create-user.form.first-name.label")} required>
                <FormInput<AddAdminFormValues>
                  name="firstName"
                  placeholder={t("page.create-user.form.first-name.placeholder")}
                  rules={{
                    validate: {
                      required: createRequiredStringRule(t, "page.create-user.form.first-name.label"),
                    },
                    maxLength: {
                      message: t("components.form.error.max-length", { length: MAX_CHARACTERS }),
                      value: MAX_CHARACTERS,
                    },
                  }}
                />
              </FormField>
              <FormField label={t("page.create-user.form.last-name.label")} required>
                <FormInput<AddAdminFormValues>
                  name="lastName"
                  placeholder={t("page.create-user.form.last-name.placeholder")}
                  rules={{
                    validate: {
                      required: createRequiredStringRule(t, "page.create-user.form.last-name.label"),
                    },
                    maxLength: {
                      message: t("components.form.error.max-length", { length: MAX_CHARACTERS }),
                      value: MAX_CHARACTERS,
                    },
                  }}
                />
              </FormField>
              <FormField label={t("page.create-user.form.mobile.label")}>
                <FormInput<AddAdminFormValues, "mobileNumber">
                  name="mobileNumber"
                  placeholder={t("page.create-user.form.mobile.placeholder")}
                  type="tel"
                  inputMode="tel"
                  rules={{
                    validate: {
                      isValid: createPhoneNumberStringRule({ t }),
                    },
                  }}
                />
              </FormField>
              <FormField label={t("page.create-user.form.email.label")} required>
                <FormInput<AddAdminFormValues, "email">
                  name="email"
                  placeholder={t("page.create-user.form.email.placeholder")}
                  type="email"
                  inputMode="email"
                  rules={{
                    validate: {
                      required: createRequiredStringRule(t, "page.create-user.form.email.label"),
                      isValid: createEmailValidRule(t),
                    },
                  }}
                />
                {emailSuggestion && (
                  <p className="mt-1 flex flex-row items-baseline text-grey-700">
                    <Trans
                      i18nKey="page.create-user.form.email.suggestion"
                      values={{ email: emailSuggestion }}
                      components={{
                        suggestion: (value) => (
                          <span className="ml-1">
                            <Button
                              styling="ghostPrimary"
                              onClick={() => {
                                form.setValue("email", emailSuggestion);
                                form.clearErrors("email");
                                posthog.capture("email_suggestion_clicked", {
                                  email: emailSuggestion,
                                });
                              }}
                            >
                              {value}
                            </Button>
                          </span>
                        ),
                      }}
                    />
                  </p>
                )}
              </FormField>
              <FormField label={t("page.create-user.form.projects.label")} required>
                <FormMultiSelect<AddAdminFormValues, "projects">
                  name="projects"
                  placeholder={t("page.create-user.form.projects.placeholder")}
                  items={allowedProjects || []}
                  sortSelectedItems={sortAlphabetically}
                  keySelector={(x) => x.id}
                  renderOption={(x) => x.name}
                  rules={{
                    required: t("components.form.error.required", {
                      inputName: t("page.create-user.form.projects.label"),
                    }),
                  }}
                />
              </FormField>
              <FormField label={t("page.create-user.form.role.label")} required>
                <FormSelect<AddAdminFormValues, UserRoleDto["type"]>
                  name="roleType"
                  placeholder={t("page.create-user.form.role.placeholder")}
                  items={roleTypes}
                  keySelector={(x) => x}
                  renderOption={(x) => getRoleTypeLabel(x)}
                  rules={{
                    required: t("components.form.error.required", {
                      inputName: t("page.create-user.form.role.label"),
                    }),
                  }}
                />
              </FormField>
              <FormField label={t("page.create-user.form.avatar.label")}>
                <FormImageInput<AddAdminFormValues, "avatar">
                  ref={fileInputRef}
                  name="avatar"
                  rules={{
                    validate: {
                      size: createImageSizeRule({ t }),
                      type: createImageTypeRule({ t }),
                    },
                  }}
                  isAvatarInput
                  cropper
                />
              </FormField>
              {sessionUser.isSuperAdmin && (
                <>
                  <FormCheckbox<AddAdminFormValues>
                    name="onboardingComplete"
                    label={t("page.create-user.form.onboarding-complete.label")}
                  />
                  <FormCheckbox<AddAdminFormValues>
                    name="isOnlyAllowedOwnTickets"
                    label={t("page.create-user.form.is-only-allowed-own-tickets.label")}
                  />
                  <FormCheckbox<AddAdminFormValues>
                    name="isAssignableToTickets"
                    label={t("page.create-user.form.is-assignable")}
                  />
                  <FormCheckbox<AddAdminFormValues> name="hideChat" label={t("page.create-user.form.chat.label")} />
                </>
              )}
            </FormContent>
            <Button className="ml-auto" type="submit" isLoading={isSubmitting} data-testid="add-admins-submit-btn">
              {t("page.create-user.form.submit.new")}
            </Button>
          </Form>
        </PageGrid.Item>
      </PageGrid.Grid>
    </DocumentPaper>
  );
}
